import { useState, useCallback } from 'react';
import { RouterLink } from '@redux-first-router-link';
import loadable from '@loadable/component';

import {
  Heading,
  Grid,
  TabList,
  Tab,
  Box,
  useTheme,
  Button,
  Text,
  ScrollPanelWithoutScrollbars,
} from '@simplywallst/ui-core';
import OpenLinkOutline from '@simplywallst/ui-core/icons/OpenLinkOutline';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { getPath } from '@/router/utils';
import { ROUTE_PORTFOLIO } from '@/constants/routes';

import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';

import { usePortfolio } from '@/hooks/portfolio/usePortfolio';
import { useIsMobileScreen } from '@/hooks/useMedia';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';

import { KeyMetric } from '@/components/KeyMetric';
import Tooltip from '@/components/Tooltip';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { MinimalDropdown } from '@/components/Dropdown';
import { PortfolioPerformanceChart } from '@/components/PortfolioPerformaceChart';
import type { PerformancePeriod } from '@/components/PortfolioPerformaceChart/types';

import { PortfolioDropdown } from './components/PortfolioDropdown';
import { PortfoliosLoadingState } from './components/PortfoliosLoadingState';
import { PortfolioMoreOptions } from './components/PortfolioMoreOptions';

import { getKeyMetricColor } from './utils';

import { StyledDiv, StyledText } from './styled';

const LimitedPortfolioBanner = loadable(
  () => import('./components/LimitedPortfolioBanner'),
  {
    resolveComponent: (c) => c.LimitedPortfolioBanner,
  }
);

const period: PerformancePeriod[] = ['7D', '1M', '3M', 'YTD', '1Y'];

type Props = {
  selectedPortfolioId: string;
  setSelectedPortfolioId: (id: string | null) => void;
};

export const Portfolios = ({
  selectedPortfolioId,
  setSelectedPortfolioId,
}: Props) => {
  const theme = useTheme();
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { mutate: mutateUser } = useUser();
  const { data: portfolio, status } = usePortfolio({ id: selectedPortfolioId });

  const { holdings, excludedHoldings } = useHoldingsLimits({
    portfolioHoldings: portfolio?.holdings || [],
  });

  const { data: userAccess } = useUserAccess();

  const [performacePeriod, setPerformancePeriod] =
    useState<PerformancePeriod>('1Y');

  const portfolioPath = getPath({
    type: ROUTE_PORTFOLIO,
    payload: { portfolioId: selectedPortfolioId },
  });

  const handleSelect = useCallback(
    (portfolioId) => {
      mutateUser({
        settings: {
          portfolio: {
            dashboard_default_portfolioId: portfolioId,
          },
        },
      });
      setSelectedPortfolioId(portfolioId);
    },
    [mutateUser, setSelectedPortfolioId]
  );

  if (status === 'pending' || !portfolio) {
    return <PortfoliosLoadingState />;
  }

  const hasLimits =
    portfolio.isAnalysisRestricted || excludedHoldings.length > 0;

  return (
    <Grid
      data-cy-id="dashboard-portfolios-section"
      gapLg="4px"
      backgroundColor="noir"
      borderRadiusLg="x1"
    >
      <StyledDiv>
        <Grid
          padding="x2"
          paddingTopLg="x1_5"
          paddingBottomLg="x2"
          paddingLeftLg="x3"
          paddingRightLg="x3"
          gap="16px"
          gapMd="8px"
        >
          <Grid
            templateColumns="auto auto"
            alignItems="start"
            alignItemsMd="center"
            justifyContent="space-between"
          >
            <Grid
              templateColumns="auto"
              templateColumnsMd={
                hasLimits ? 'minmax(auto, 40%) max-content' : 'auto'
              }
              alignItems="center"
            >
              <PortfolioDropdown
                portfolioId={selectedPortfolioId}
                onSelect={handleSelect}
              >
                <Heading as="h1" typography="baseStrong" isTruncated>
                  {portfolio.name}
                </Heading>
              </PortfolioDropdown>
              {portfolio.isAnalysisRestricted ? (
                <LimitedPortfolioBanner content="This portfolio is restricted due to plan limits" />
              ) : excludedHoldings.length > 0 ? (
                <LimitedPortfolioBanner
                  content={`${excludedHoldings.length} ${pluralise(
                    excludedHoldings.length,
                    'holding',
                    'holdings'
                  )} excluded due to ${userAccess.planType} plan limit`}
                />
              ) : null}
            </Grid>
            <Grid
              templateColumns="max-content auto"
              alignItems="center"
              paddingTop="x0_5"
              paddingTopMd="x0"
            >
              <RouterLink to={portfolioPath} noUnderline>
                <Button styleType="passive">
                  <OpenLinkOutline fill={theme.color.whiteRGBA(0.5)} />
                </Button>
              </RouterLink>
              <Grid>
                <PortfolioMoreOptions portfolio={portfolio} />
              </Grid>
            </Grid>
          </Grid>
          <ScrollPanelWithoutScrollbars overflow="auto">
            <Grid templateColumns="repeat(3, max-content)" gap="32px">
              <KeyMetric>
                <Text typography="xLargeStrong" data-cy-id="total-value">
                  {portfolio.isAnalysisRestricted
                    ? 'n/a'
                    : formatCurrencyToScaleTruncated(
                        portfolio.analysis.value,
                        portfolio.currencyISO
                      )}
                </Text>
                <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                  <Grid templateColumns="max-content auto" gap="4px">
                    <Tooltip
                      title={
                        <Box maxWidth="260px" padding="x1">
                          <Text
                            textAlign="center"
                            typography="tiny"
                            color="spaceGray"
                          >
                            This represents the combined worth of all your
                            holdings in this portfolio.
                          </Text>
                        </Box>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <StyledText>
                        <Text
                          as="span"
                          typography="tiny"
                          color="whiteRGBA"
                          opacity={0.7}
                        >
                          Total Value
                        </Text>
                      </StyledText>
                    </Tooltip>
                    •{' '}
                    {portfolio.isAnalysisRestricted
                      ? '0 holdings'
                      : `${holdings.length} ${pluralise(
                          holdings.length,
                          'holding',
                          'holdings'
                        )}`}
                  </Grid>
                </Text>
              </KeyMetric>
              <KeyMetric
                color={
                  portfolio.isAnalysisRestricted
                    ? getKeyMetricColor(0)
                    : getKeyMetricColor(portfolio.analysis.roi)
                }
              >
                <Text typography="xLargeStrong" data-cy-id="roi-value">
                  {portfolio.isAnalysisRestricted
                    ? 'n/a'
                    : formatCurrencyToScaleTruncated(
                        portfolio.analysis.roiValue,
                        portfolio.currencyISO
                      )}
                </Text>
                <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                  <Grid templateColumns="max-content auto" gap="4px">
                    <Tooltip
                      title={
                        <Box maxWidth="260px" padding="x1">
                          <Text
                            textAlign="center"
                            typography="tiny"
                            color="spaceGray"
                          >
                            This indicates the unrealised profits or losses from
                            all your holdings in this portfolio.
                          </Text>
                        </Box>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <StyledText>
                        <Text
                          as="span"
                          typography="tiny"
                          color="whiteRGBA"
                          opacity={0.7}
                        >
                          Total Returns
                        </Text>
                      </StyledText>
                    </Tooltip>
                    {portfolio.isAnalysisRestricted ? null : (
                      <PositiveNegativeValue value={portfolio.analysis.roi}>
                        {formatterPercentage(portfolio.analysis.roi)}
                      </PositiveNegativeValue>
                    )}
                  </Grid>
                </Text>
              </KeyMetric>
              <KeyMetric color="darkerGray">
                <Grid
                  templateColumns="max-content max-content"
                  gap="4px"
                  alignItems="end"
                >
                  <Text typography="xLargeStrong" data-cy-id="dividend">
                    {portfolio.isAnalysisRestricted
                      ? 'n/a'
                      : formatCurrencyToScaleTruncated(
                          portfolio.analysis.estimatedYearlyDividend,
                          portfolio.currencyISO
                        )}
                  </Text>
                  <Text typography="baseStrong" color="whiteRGBA" opacity={0.5}>
                    /yr
                  </Text>
                </Grid>
                <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                  <Grid templateColumns="max-content auto" gap="4px">
                    <Tooltip
                      title={
                        <Box maxWidth="260px" padding="x1">
                          <Text
                            textAlign="center"
                            typography="tiny"
                            color="spaceGray"
                          >
                            This figure represents the average dividend yield
                            expected from your portfolio. It provides an
                            estimate of the yearly income you can potentially
                            earn based on the dividend payments of the
                            underlying stocks.
                          </Text>
                        </Box>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <StyledText>
                        <Text
                          as="span"
                          typography="tiny"
                          color="whiteRGBA"
                          opacity={0.7}
                        >
                          Est. Dividend
                        </Text>
                      </StyledText>
                    </Tooltip>
                    {portfolio.isAnalysisRestricted
                      ? null
                      : `• ${formatterPercentage(
                          portfolio.analysis.estimatedDividendYield
                        )} Yield`}
                  </Grid>
                </Text>
              </KeyMetric>
            </Grid>
          </ScrollPanelWithoutScrollbars>
        </Grid>
      </StyledDiv>
      <Box
        position="relative"
        padding="x2"
        paddingTopLg="x2"
        paddingBottomLg="x2"
        paddingRightLg="x3"
        paddingLeftLg="x3"
      >
        {isMobileScreen ? (
          <Box
            position="absolute"
            top="14px"
            right="24px"
            backgroundColor="noir"
            padding="x0_5"
            borderRadius="x1"
          >
            <MinimalDropdown
              displayType="inline-block"
              name="period-dropdown"
              options={period.map((n) => ({ label: n, option: n }))}
              onSelect={(option) =>
                setPerformancePeriod(option as PerformancePeriod)
              }
              label={performacePeriod}
              value={performacePeriod}
              underline={false}
            />
          </Box>
        ) : (
          <Box
            position="absolute"
            right="16px"
            width="248px"
            backgroundColor="noir"
            padding="x0_5"
            borderRadius="x1"
          >
            <TabList templateColumns="repeat(5, auto)">
              {period.map((n) => (
                <Tab
                  key={`period-tab-${n}`}
                  styleType="period"
                  selected={performacePeriod === n}
                  onClick={() => setPerformancePeriod(n)}
                  value={n}
                >
                  {n}
                </Tab>
              ))}
            </TabList>
          </Box>
        )}
        <PortfolioPerformanceChart
          isAnalysisRestricted={portfolio.isAnalysisRestricted}
          market={portfolio.analysis.marketCountryISO}
          portfolioId={portfolio.id}
          period={performacePeriod}
        />
      </Box>
    </Grid>
  );
};
